import { useState, useEffect } from "react";
import { Button, Modal, List, Popconfirm, Select, message } from "antd";
import styled from "styled-components";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { DeleteTwoTone, FolderOpenOutlined, ProfileOutlined } from "@ant-design/icons";
import { Slide } from "../../../api/slideAPI";
import { MediaGroup } from "../../../api/mediaGroupAPI";
import { PostCase, assignToPostCase, unassignFromPostCase, postCaseDisplayName } from "../../../api/postCaseAPI";
import { useMediaMultiSelect } from "./mediaMultiSelect";
import MediaSelectionToolbar from "./MediaSelectionToolbar";
import MediaDragBadge from "./MediaDragBadge";
import MediaGroupAssignModal from "./MediaGroupAssignModal";
import CaseAssignModal from "./CaseAssignModal";
import { groupIdOf } from "../mediaGroup";

interface Props {
  open: boolean;
  orderSlideList: Slide[];
  onOk: () => void;
  onCancel: () => void;
  onDragEnd: (result: any, selectedIds: number[]) => void;
  onDeleteSlide: (slideId: number) => void;
  onDeleteMany: (ids: number[]) => void;
  cases?: PostCase[];
  onCaseAssigned?: () => void;
  groups?: MediaGroup[];
  onAssignGroup?: (
    mediaIds: number[],
    targetGroupId: number | null,
    newGroupName?: string
  ) => Promise<void>;
}

const SlideEditModal = ({
  open,
  orderSlideList,
  onOk,
  onCancel,
  onDragEnd,
  onDeleteSlide,
  onDeleteMany,
  cases = [],
  onCaseAssigned,
  groups,
  onAssignGroup,
}: Props) => {
  const UNASSIGN_VALUE = "__unassign__";
  const hasCases = (cases?.length ?? 0) > 0;
  const {
    selectedIds,
    clear,
    handleRowClick,
    handleRowMouseDown,
    rangeRowProps,
    scrollRef,
    setDraggingId,
    rowState,
  } = useMediaMultiSelect(
    open,
    orderSlideList.map((item) => item.slideId)
  );

  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [caseModalOpen, setCaseModalOpen] = useState(false);

  // 선택한 항목들의 Case 선택값을 한꺼번에 바꾼다 (실제 배정은 기존과 동일하게 OK 시점)
  const applyCaseToSelected = (value: number | string) => {
    setPendingChanges((prev) => {
      const next = { ...prev };
      selectedIds.forEach((id) => {
        next[id] = value;
      });
      return next;
    });
  };

  // 행에 현재 소속 그룹명을 함께 보여준다 (배정 결과 확인용)
  const groupNameOf = (slide: any): string | null => {
    const gid = groupIdOf(slide);
    if (gid == null) return null;
    return groups?.find((g) => g.mediaGroupId === gid)?.name ?? null;
  };

  const [pendingChanges, setPendingChanges] = useState<Record<number, string | number>>({});

  useEffect(() => {
    if (open) {
      setPendingChanges({});
    }
  }, [open]);

  const handleCaseChange = (slide: Slide, value: number | string) => {
    if (!slide.slideId) return;
    setPendingChanges((prev) => ({
      ...prev,
      [slide.slideId!]: value,
    }));
  };

  const handleOk = async () => {
    const assignIdsByCase: Record<number, number[]> = {};
    const unassignIds: number[] = [];
    const currentIds = new Set(orderSlideList.map((s) => s.slideId));

    Object.entries(pendingChanges).forEach(([idStr, caseId]) => {
      const slideId = Number(idStr);
      if (!currentIds.has(slideId)) return;

      if (caseId === UNASSIGN_VALUE) {
        unassignIds.push(slideId);
      } else {
        const cId = Number(caseId);
        if (!assignIdsByCase[cId]) assignIdsByCase[cId] = [];
        assignIdsByCase[cId].push(slideId);
      }
    });

    let isChanged = false;

    try {
      if (unassignIds.length > 0) {
        await unassignFromPostCase({ slideIds: unassignIds });
        isChanged = true;
      }

      for (const [cId, sIds] of Object.entries(assignIdsByCase)) {
        await assignToPostCase({
          postCaseId: Number(cId),
          slideIds: sIds,
        });
        isChanged = true;
      }

      if (isChanged) {
        message.success("Case 배정이 업데이트되었습니다.");
        onCaseAssigned?.();
      }
    } catch (error) {
      message.error("Case 배정 업데이트 중 오류가 발생했습니다.");
      console.error(error);
    }

    onOk();
  };

  return (
    <Modal
      title="Edit slides"
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      centered
      width={560}
      styles={{ body: { padding: 0 } }}
    >
      <MediaSelectionToolbar
        selectedCount={selectedIds.size}
        itemLabel="슬라이드"
        onDeleteSelected={() => {
          onDeleteMany(Array.from(selectedIds));
          clear();
        }}
        onClear={clear}
        extraActions={
          <>
            {hasCases && (
              <Button size="small" icon={<ProfileOutlined />} onClick={() => setCaseModalOpen(true)}>
                Case 배정
              </Button>
            )}
            {onAssignGroup && (
              <Button size="small" icon={<FolderOpenOutlined />} onClick={() => setGroupModalOpen(true)}>
                그룹 지정
              </Button>
            )}
          </>
        }
      />
      <ScrollBody ref={scrollRef}>
        <DragDropContext
          onDragStart={(start) =>
            setDraggingId(orderSlideList[start.source.index]?.slideId ?? null)
          }
          onDragEnd={(r) => {
            setDraggingId(null);
            onDragEnd(r, Array.from(selectedIds));
          }}
        >
          <Droppable droppableId="viewerSlides">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                <List
                  itemLayout="horizontal"
                  dataSource={orderSlideList}
                  renderItem={(slide, idx) => {
                    const rawCaseId = pendingChanges[slide.slideId!] !== undefined 
                      ? pendingChanges[slide.slideId!] 
                      : (slide as any).postCaseId;

                    let selectValue = (rawCaseId === null || rawCaseId === undefined || rawCaseId === UNASSIGN_VALUE)
                      ? UNASSIGN_VALUE 
                      : Number(rawCaseId);

                    if (selectValue !== UNASSIGN_VALUE && !cases.some(c => c.postCaseId === selectValue)) {
                      selectValue = UNASSIGN_VALUE;
                    }

                    return (
                      <Draggable
                        key={`viewerSlides_${idx}`}
                        draggableId={`viewerSlides_${idx}`}
                        index={idx}
                      >
                        {(provided, snapshot) => {
                          const rs = rowState(slide.slideId, snapshot.isDragging);
                          return (
                          <List.Item
                            ref={provided.innerRef}
                            {...provided.dragHandleProps}
                            {...provided.draggableProps}
                            {...rangeRowProps(idx)}
                            onMouseDown={(e) => handleRowMouseDown(e, idx)}
                            onClick={(e) => handleRowClick(e, slide.slideId)}
                            style={{
                              ...(provided.draggableProps.style || {}),
                              background: rs.isSelected ? "#e6f4ff" : undefined,
                              opacity: rs.isGhosting ? 0.35 : undefined,
                              cursor: "pointer",
                            }}
                          >
                            {rs.showBadge && <MediaDragBadge count={rs.badgeCount} />}
                            <List.Item.Meta
                              avatar={
                                <img
                                  src={
                                    process.env.REACT_APP_BACKEND_URL +
                                    "/api/photo/" +
                                    slide.thumbnail
                                  }
                                  alt={slide?.slidename}
                                  style={{
                                    width: "50px",
                                    height: "50px",
                                    objectFit: "cover",
                                    ...(slide.qcResult === 'FAIL' ? { outline: '1.5px solid #ff4d4f' } : {}),
                                  }}
                                />
                              }
                              title={
                                <p style={{ margin: "0" }}>
                                  {slide?.slidename || slide?.caption}
                                  {groupNameOf(slide) && <GroupChip>{groupNameOf(slide)}</GroupChip>}
                                </p>
                              }
                            />
                            {hasCases && (
                              <Select
                                size="small"
                                style={{ width: 140, marginRight: 12 }}
                                value={selectValue}
                                onChange={(v) => handleCaseChange(slide, v)}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Select.Option value={UNASSIGN_VALUE}>미배정</Select.Option>
                                {cases!.map((pc, i) => (
                                  <Select.Option key={pc.postCaseId} value={pc.postCaseId}>
                                    {pc.organ || postCaseDisplayName(pc, i)}
                                  </Select.Option>
                                ))}
                              </Select>
                            )}
                            <Popconfirm
                              title="Delete the slide"
                              description="Are you sure to delete this slide?"
                              onConfirm={() => slide?.slideId && onDeleteSlide(slide.slideId)}
                              okText="Ok"
                              cancelText="Cancel"
                            >
                              <DeleteTwoTone
                                style={{
                                  marginLeft: "2rem",
                                  fontSize: "1.25rem",
                                  cursor: "pointer"
                                }}
                              />
                            </Popconfirm>
                          </List.Item>
                          );
                        }}
                      </Draggable>
                    );
                  }}
                />
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </ScrollBody>

      <MediaGroupAssignModal
        open={groupModalOpen}
        groups={groups ?? []}
        selectedCount={selectedIds.size}
        itemLabel="슬라이드"
        onCancel={() => setGroupModalOpen(false)}
        onSubmit={async ({ targetGroupId, newGroupName }) => {
          await onAssignGroup?.(Array.from(selectedIds), targetGroupId, newGroupName);
          setGroupModalOpen(false);
          clear();
        }}
      />

      <CaseAssignModal
        open={caseModalOpen}
        cases={cases}
        selectedCount={selectedIds.size}
        itemLabel="슬라이드"
        onCancel={() => setCaseModalOpen(false)}
        onSubmit={(value) => {
          applyCaseToSelected(value);
          setCaseModalOpen(false);
          clear();
        }}
      />
    </Modal>
  );
};

const GroupChip = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 8px;
  background: #f0f5ff;
  color: #2f54eb;
  font-size: 11px;
  line-height: 1.6;
  vertical-align: middle;
`;

const ScrollBody = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  padding: 16px 24px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #bfbfbf;
  }

  scrollbar-width: thin;
  scrollbar-color: #d9d9d9 transparent;
`;

export default SlideEditModal;